'use strict';

/**
 * Domain Object definitions and prototypes.
 */

(function() {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.domain = window.atex.aceui.domain || {};

  atex.aceui.domain.DomainObjectDefinitionInfo = function(domainObjectDefinition) {
    if (typeof domainObjectDefinition === 'undefined') {
      throw atex.aceui.error.Error({
        origin: 'atex.aceui.domain.DomainObjectDefinitionInfo',
        severity: atex.aceui.error.severities.MAJOR,
        type: atex.aceui.error.types.INVALID_PARAMETERS,

        cause: "Cannot create atex.aceui.domain.DomainObjectDefinitionInfo prototype with" +
               " undefined parameters ('domainObjectDefinition': " + domainObjectDefinition + ")!"
      });
    }

    var definitionInfo = Object.create(Object.prototype);

    var children = {};

    definitionInfo.getChildren = function() {
      return children;
    };

    definitionInfo.getChild = function(name) {
      return children[name];
    };

    definitionInfo.removeChild = function(name) {
      delete children[name];
    };

    definitionInfo.addChild = function(name, child) {
      children[name] = child;
    };

    definitionInfo.getDefinition = function() {
      return domainObjectDefinition;
    };

    definitionInfo.toString = function() {
      var childKeys = Object.keys(children);

      var result = "DomainObjectDefinitionInfo { id: '" + definitionInfo.getId() + "' type: '" + domainObjectDefinition.getTypeId() + "'";

      childKeys.forEach(function(childKey) {
        result += (", " + childKey + ": " + children[childKey].toString());
      });

      result += " }";

      return result;
    };

    /**
     * We freeze the created prototype because we don't want anyone
     * to mess around (modify) with it.
     */
    Object.freeze(definitionInfo);

    return definitionInfo;
  };

  atex.aceui.domain.DomainObjectDefinition = function(id, typeId, arrayTypeName) {
    if (typeof id === 'undefined') {
      throw atex.aceui.error.Error({
        origin: 'atex.aceui.domain.DomainObjectDefinition',
        severity: atex.aceui.error.severities.MAJOR,
        type: atex.aceui.error.types.INVALID_PARAMETERS,

        cause: "Cannot create atex.aceui.domain.DomainObjectDefinition prototype with" +
               " undefined parameters ('domainObjectId': " + id + ")!"
      });
    }

    var domainObjectDefinition = Object.create(Object.prototype);

    domainObjectDefinition.getId = function() {
      return id;
    };

    domainObjectDefinition.getTypeId = function() {
      return typeId;
    };

    domainObjectDefinition.getArrayTypeName = function() {
      return arrayTypeName;
    };

    domainObjectDefinition.isList = function() {
      return false;
    };

    /***************************************************************/
    /* Default implementations of Domain Object Definition methods */
    /***************************************************************/

    domainObjectDefinition.customDefinition = {};
    domainObjectDefinition.createEmptyData = undefined;
    domainObjectDefinition.customizeDefinition = function() {};

    /**
     * We seal the created prototype object because we don't want anyone to mess
     * around with it. Extension of domain object types are made by using this prototype
     * with Object.create(...), and you should never modify the existing prototype
     * properties.
     */
    Object.seal(domainObjectDefinition);

    return domainObjectDefinition;
  };

  atex.aceui.domain.DomainObject = function(dataAccessor, domainObjectDefinitionInfo) {
    if (typeof dataAccessor === 'undefined' || typeof domainObjectDefinitionInfo === 'undefined') {
      throw atex.aceui.error.Error({
        origin: 'atex.aceui.domain.DomainObject',
        severity: atex.aceui.error.severities.MAJOR,
        type: atex.aceui.error.types.INVALID_PARAMETERS,

        cause: "Cannot create atex.aceui.domain.DomainObject prototype with" +
               " undefined parameters ('dataAccessor': " + dataAccessor + ", 'domainObjectDefinitionInfo': " + domainObjectDefinitionInfo + ")!"
      });
    }

    var domainObjectDefinition = domainObjectDefinitionInfo.getDefinition();

    var domainObject = {};

    domainObject.aceuiID = atex.aceui.Utils.uuid();

    var children = {};
    var eventListeners = {};

    var needsDigest = false;
    var lastModifierId = undefined;

    /**
     * Knowledge of whether or not a Domain Object node is actually the root in
     * a tree is needed to work correctly in the case where you are replacing the
     * entire data shuttle by using setData(...).
     */

    Object.defineProperty(domainObject, 'isRoot', {
      get: function() { return this._isRoot || false; },
      set: function(isRoot) { this._isRoot = isRoot; }
    });

    /**
     * Every Domain Object has an internal pointer to it's logical parent. The
     * parent of the top Domain Object of the Domain Object tree is undefined.
     */

    Object.defineProperty(domainObject, 'parent', {
      get: function() { return this._parent; },
      set: function(parent) { this._parent = parent; }
    });

    /**
     * The Domain Object might know it's location in the total Domain Object tree.
     * This information is purely for debugging purposes.
     */

    Object.defineProperty(domainObject, 'path', {
      get: function() { return this._path; },
      set: function(path) { this._path = path; }
    });

    domainObject.getDefinition = function() {
      return domainObjectDefinition;
    };

    domainObject.getChildren = function() {
      return children;
    };

    domainObject.hasChildren = function() {
      return Object.keys(children).length > 0;
    };

    domainObject.getChild = function(name) {
      return children[name];
    };

    domainObject.addChild = function(name, childDomainObject) {
      children[name] = childDomainObject;
    };

    domainObject.hasChild = function(name) {
      return typeof children[name] !== 'undefined';
    };

    domainObject.markAsDirty = function(callerId) {
      lastModifierId = callerId || this.aceuiID;

      needsDigest = true;

      if (typeof this.parent !== 'undefined') {
        this.parent.markAsDirty(lastModifierId);
      }
    };

    domainObject.isDirty = function() {
      return (typeof lastModifierId !== 'undefined');
    };

    /*******************************/
    /* Event system implementation */
    /*******************************/

    domainObject.on = function(eventName, listenerFn) {
      eventListeners[eventName] = eventListeners[eventName] || [];
      eventListeners[eventName].push(listenerFn);

      return function() {
        var listeners = eventListeners[eventName] || [];
        var listenerIndex = listeners.indexOf(listenerFn);

        listeners.splice(listenerIndex, 1);
      };
    };

    domainObject.fire = function(eventName) {
      var listeners = eventListeners[eventName] || [];

      for (var i = 0, length = listeners.length; i < length; i++) {
        listeners[i].apply(null, [{ eventName: eventName }].concat(Array.prototype.slice.call(arguments, 1)));
      }
    };

    var maxDigestCount = 10;

    domainObject.changed = function() {
      var root = this.parent || this;

      while (typeof root.parent !== 'undefined') {
        root = root.parent;
      }

      var digestCount = 0;

      while (root.digest()) {
        if (++digestCount >= maxDigestCount) {
          var error = atex.aceui.error.Error({
            origin: 'atex.aceui.domain.DomainObject',
            type: atex.aceui.error.types.RUNTIME,
            severity: atex.aceui.error.severities.MAJOR,

            cause: 'Data model failed to stabilize before reaching the max digest count (' + maxDigestCount + ')! ' +
                   'The most likely cause for this behaviour is one or more domain objects configured in a circular ' +
                   'dependency loop.'
          });

          root.fire('aceui:error', error);

          return;
        }
      }

      root.digested();
    };

    domainObject.digest = function() {
      needsDigest = undefined;

      if (typeof this.onDigest !== 'undefined') {
        needsDigest = this.onDigest() || needsDigest;
      }

      Object.keys(children).forEach(function(child) {
        needsDigest = children[child].digest() || needsDigest;
      });

      return needsDigest;
    };

    domainObject.digested = function() {
      Object.keys(children).forEach(function(child) {
        children[child].digested();
      });

      this.fire('aceui:digested');

      if (domainObject.isDirty()) {
        this.fire('aceui:changed', lastModifierId);
      }

      lastModifierId = undefined;
    };

    domainObject.getData = function() {

      var data = dataAccessor.value;
      return (typeof data !== 'undefined') ? JSON.parse(JSON.stringify(data)) : data;
    };

    domainObject.setDirty = function(callerId) {
      lastModifierId = callerId || this.aceuiID;
      needsDigest = true;

      Object.keys(children).forEach(function(child) {
        children[child].setDirty(callerId);
      });
    };

    domainObject.setData = function(data, callerId) {
      if (typeof data === 'undefined' && typeof domainObjectDefinition.createEmptyData !== 'undefined') {
        data = domainObjectDefinition.createEmptyData();
      } else {
        data = (typeof data !== 'undefined') ? JSON.parse(JSON.stringify(data)) : data;
      }

      dataAccessor.value = data;
      this.markAsDirty(callerId);

      if (this.isRoot === true) {
        this.reOriginate(data);
      }

      if (typeof data !== 'undefined') {
        Object.keys(children).forEach(function(child) {
          children[child].setData(atex.aceui.ObjectUtils.getByPath(data, [child]), callerId);
        });
      } else {
        this.setDirty(callerId);
      }
    };

    domainObject.reOriginate = function(newRootObject) {
      dataAccessor.root = newRootObject;

      Object.keys(children).forEach(function(child) {
        children[child].reOriginate(newRootObject);
      });
    };

    domainObject.initSelf = function(resolvedRequirements) {};

    domainObject.init = function(requirements) {
      var myRequirements = requirements || {};

      Object.keys(children).forEach(function(child) {
        var childRequirements = myRequirements[child] || {};
        children[child].init(childRequirements);
      });

      try {
        this.initSelf(myRequirements._requirements);
      }  catch (error) {
        throw atex.aceui.error.Error({
          origin: 'atex.aceui.domain.DomainObject',
          severity: atex.aceui.error.severities.MAJOR,
          type: atex.aceui.error.types.UNKNOWN,

          rootCause: error,

          cause: "An error was thrown while initializing Domain Object '" + domainObject.getDefinition().getId() + "'"
        });
      }
    };

    domainObject.destroySelf = function() {};

    domainObject.destroy = function() {
      Object.keys(children).forEach(function(child) {
        children[child].destroy();
      });

      try {
        this.destroySelf();
      } catch (error) {
        throw atex.aceui.error.Error({
          origin: 'atex.aceui.domain.DomainObject',
          severity: atex.aceui.error.severities.MAJOR,
          type: atex.aceui.error.types.UNKNOWN,

          rootCause: error,

          cause: "An error was thrown while destroying Domain Object '" + domainObject.getDefinition().getId() + "'"
        });
      }
    };

    domainObject.packSelf = function(data) {
      return data;
    };

    domainObject.pack = function() {
      var resultObject = dataAccessor.value;

      if (typeof resultObject !== 'undefined') {
        resultObject = JSON.parse(JSON.stringify(resultObject));

        Object.keys(children).forEach(function(child) {
          resultObject[child] = children[child].pack();
        });
      }

      try {
        return this.packSelf(resultObject);
      }  catch (error) {
        throw atex.aceui.error.Error({
          origin: 'atex.aceui.domain.DomainObject',
          severity: atex.aceui.error.severities.MAJOR,
          type: atex.aceui.error.types.UNKNOWN,

          rootCause: error,

          cause: "An error was thrown while packing data" +
                 " for Domain Object '" + domainObject.getDefinition().getId() + "'"
        });
      }
    };

    domainObject.toString = function() {
      var childKeys = Object.keys(children);
      var result = "DomainObject instance { id: '" + domainObjectDefinition.getId() + "'";

      if (typeof domainObjectDefinition.getTypeId() !== 'undefined') {
        result += ", type: '" + domainObjectDefinition.getTypeId() + "'";
      }

      if (typeof this.path !== 'undefined') {
        result += ", path: '" + this.path + "'";
      }

      childKeys.forEach(function(childKey) {
        result += (", " + childKey + ": " + children[childKey].toString());
      });

      result += " }";

      return result;
    };

    /**
     * We seal the created prototype object because we don't want anyone to mess
     * around with it. Extension of domain objects are made by using this prototype
     * with Object.create(...), and you should never modify the existing prototype
     * properties.
     */
    Object.seal(domainObject);

    return domainObject;
  };
})();
